import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Clock, Users } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import { PROGRAM_SLUGS } from "../data/siteContent";

export default function Courses() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="courses-page">
      <PageHero
        overline="Academic Programs"
        title={<>Programs built for <span className="italic text-gold-gradient">real</span> care</>}
        subtitle="Choose from our INC-aligned nursing programs, each combining theory with extensive clinical practice."
      />

      <section className="bg-sand py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-8">
          {courses.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.06}>
              <motion.div
                whileHover={{ scale: 1.005 }}
                transition={{ duration: 0.3 }}
                className="grid lg:grid-cols-12 gap-8 bg-white border border-navy/10 rounded-[2px] p-8 lg:p-12"
                data-testid={`course-detail-${c.short_name}`}
              >
                <div className="lg:col-span-3">
                  <div className="font-serif text-6xl text-gold/30">{c.short_name}</div>
                  <div className="mt-4 flex flex-col gap-2 text-sm text-navy/60">
                    <span className="inline-flex items-center gap-2"><Clock size={16} className="text-gold" /> {c.duration}</span>
                    {c.seats && <span className="inline-flex items-center gap-2"><Users size={16} className="text-gold" /> {c.seats}</span>}
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <h3 className="font-serif text-3xl text-navy mb-4">{c.title}</h3>
                  <p className="text-navy/60 leading-relaxed mb-6">{c.description}</p>
                  {c.eligibility && (
                    <p className="text-sm text-navy/70"><span className="font-semibold text-navy">Eligibility:</span> {c.eligibility}</p>
                  )}
                </div>
                <div className="lg:col-span-3">
                  <ul className="space-y-3">
                    {(c.highlights || []).map((h, idx) => (
                      <li key={idx} className="flex gap-2 text-sm text-navy/70">
                        <Check size={18} className="text-gold shrink-0" /> {h}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/programs/${PROGRAM_SLUGS[c.short_name] || ""}`}
                    data-testid={`course-view-details-${c.short_name}`}
                    className="mt-6 inline-flex items-center gap-2 text-navy font-semibold hover:text-gold transition-colors"
                  >
                    View program details <ArrowUpRight size={16} />
                  </Link>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-navy py-20 text-center relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-2xl mx-auto px-5">
          <h2 className="font-serif text-3xl sm:text-4xl text-sand mb-6">Ready to enrol?</h2>
          <Link to="/admissions" className="inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold px-8 py-4 transition-transform duration-300 hover:scale-105">
            Apply for 2026-27 <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
